import { useCallback, useEffect, useRef, useState } from 'react';
import useDashboard from '../../hooks/useDashboard';

// Loads a single widget's data from the backend (which serves it out of the
// shared statistic cache, so many clients asking for the same day + branch
// scope cost one computation). Exposes loading/error and a manual refresh.
export default function useWidgetData(widgetId, { enabled = true } = {}) {
  const { getWidgetData } = useDashboard();
  const [payload, setPayload] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(
    async ({ forceRecompute = false } = {}) => {
      if (!enabled) return;
      setLoading(true);
      setError(null);
      try {
        const res = await getWidgetData(widgetId, { forceRecompute });
        if (!mountedRef.current) return;
        setPayload(res?.payload ?? null);
        setMeta({
          computedAt: res?.computedAt || null,
          expiresAt: res?.expiresAt || null,
          branchScope: res?.branchScope || null,
          day: res?.day || null,
        });
      } catch (err) {
        if (mountedRef.current) setError(err);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [enabled, getWidgetData, widgetId]
  );

  useEffect(() => {
    load();
  }, [load]);

  return { payload, meta, loading, error, refresh: load };
}
